import axios from 'axios';
import type {
  AxiosErrorInterface,
  ErrorTitlesIds,
} from '@/interfaces/base/axiosError.interface';
import type { NotificationInterface } from '@/interfaces/components/notification/notification.interface';

/**
 * @deprecated Use o novo arquivo que esta presente em utils/useErrorMessage.ts
 */
export const errorTypes: Record<ErrorTitlesIds, string> = {
  LOGIN_ERROR: 'Falha ao fazer login',
  VALIDATION_ERROR: 'Erro de validação',
  PERSON_DUPLICATED_ERROR: 'CPF duplicado',
  PAYMENT_REFUSED_ERROR: 'Erro no pagamento',
  UNAUTHENTICATED_ERROR: 'Erro de autenticação',
  INVALID_OPERATION_VALUE_ERROR: 'Valor inválido',
  ENTITY_NOT_FOUND_ERROR: 'Registro não encontrado',
  SUBSCRIPTION_NOT_FOUND_ERROR: 'Conta indisponível',
  SUBSIDIARY_IS_NOT_ACTIVE_ERROR: 'Cadastro inativo',
  DATABASE_ERROR: 'Erro ao acessar o banco de dados',
  CREDIT_ANALYSIS_ERROR: 'Erro ao solicitar análise de crédito',
  LEGAL_ANALYSIS_ERROR: 'Erro ao solicitar análise jurídica',
  SUBSCRIPTION_ALREADY_EXISTS_ERROR: 'Assinatura duplicada',
  INTERNAL_SERVER_ERROR: 'Falha na comunicação com o servidor',
  PAYMENT_REFUSED_ADMINISTRATOR_ERROR: 'Erro ao encontrar assinatura',
  SUBSIDIARY_ALREADY_EXISTS_WITH_DOCUMENT_ERROR: 'Cadastro duplicado',
  USER_POLICIES_NOT_ACCEPTED_ERROR: 'Política de privacidade não aceita',
  SUBSCRIPTION_NOT_FOUND_ADMINISTRATOR_ERROR: 'Erro ao encontrar assinatura',
};

/**
 * Trata o erro do axios
 * @param error Error do try catch
 * @param message Mensagem opcional caso o erro não seja resolvido
 * @returns notificação com as informações do erro
 */
export const useErrorMessage = (
  error: unknown,
  message: string,
): { notification: NotificationInterface } => {
  if (!axios.isAxiosError<AxiosErrorInterface>(error)) {
    return {
      notification: {
        message: message,
        type: 'error',
        title: 'Erro desconhecido',
        showMessage: true,
      },
    };
  }

  const hasResponse = error.response && error.response.data;
  const isDataObject =
    hasResponse &&
    typeof error.response.data === 'object' &&
    !Array.isArray(error.response.data) &&
    error.response.data !== null;

  if (error.response && !isDataObject) {
    const status = error.response.status;
    let title = 'Erro desconhecido';
    let errorMessage = message;

    if (status === 404) {
      title = 'Recurso não encontrado';
      errorMessage =
        'O recurso solicitado não foi encontrado. Verifique a URL e tente novamente.';
    } else if (status >= 500) {
      title = 'Houve um erro interno no servidor';
      errorMessage =
        'O servidor encontrou um erro ao processar sua solicitação. Por favor, tente novamente mais tarde.';
    } else if (status === 401) {
      title = 'Não autorizado';
      errorMessage = 'Você não tem permissão para acessar este recurso.';
    } else if (status === 403) {
      title = 'Acesso negado';
      errorMessage = 'Acesso negado a este recurso.';
    } else {
      errorMessage = error.message || message;
    }

    return {
      notification: {
        message: errorMessage,
        type: 'error',
        title: title,
        showMessage: true,
      },
    };
  }

  /**
   * Se erro de validação, retorna a mensagem de erro
   */
  if (
    isDataObject &&
    'type' in error.response.data &&
    'errors' in error.response.data &&
    Array.isArray(error.response.data.errors) &&
    error.response.data.errors.length > 0
  ) {
    return {
      notification: {
        message: error.response.data.errors[0]?.message ?? 'Erro de validação',
        type: 'error',
        title: errorTypes[error.response.data.type] ?? error.response.data.type,
        showMessage: true,
        id: error.response.data.type,
      },
    };
  }

  if (
    isDataObject &&
    'errors' in error.response.data &&
    Array.isArray(error.response.data.errors) &&
    error.response.data.errors.length > 0 &&
    'title' in error.response.data.errors[0]
  ) {
    return {
      notification: {
        message: error.response.data.errors[0].message,
        type: 'error',
        title: error.response.data.errors[0].title,
        showMessage: true,
      },
    };
  }

  if (
    isDataObject &&
    'message' in error.response.data &&
    'type' in error.response.data
  ) {
    return {
      notification: {
        message: error.response?.data.message ?? error.message,
        type: 'error',
        title: error.response.data.type
          ? errorTypes[error.response.data.type]
          : 'Erro',
        showMessage: true,
        id: error.response.data.type,
      },
    };
  }

  return {
    notification: {
      message: message,
      type: 'error',
      title: 'Erro desconhecido',
      showMessage: true,
    },
  };
};
